import React, { Fragment, useEffect, FC, useCallback, useReducer, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../../axios';
import useTurbinePath from '../../Shared/UseTurbinePathHook';
import { reducer, initialState, ImagesResponse } from './Stitching.reducer';
import styles from './Stitching.module.scss';
import CanvasContainer from './CanvasContainer';
import { MatchTurbinePathParams } from '../../../shared/models/matchTurbinePathParams';
import TreeView from '../../Shared/TreeView/STTreeView';
import { StitchingManipulationRules, sortImagesAscByTopFunction } from '../../../shared/Helpers';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';
import { describeRestoreNoOp, isRestoreQueued } from '../../Shared/restoreOutcome';
import { Dropdown } from 'react-bootstrap';
import { EllipsisOutlined } from '@ant-design/icons';
import ConfirmationDialog from '../Canvas/ConfirmationDialog';
import { Button } from 'antd';
import * as Utils from 'hermes-tiles-grid-popup';
import { RestoreModal, RestoreSuccessModal } from '../../Shared/RestoreModal';

const thumbnailLongPoolingInterval = 5000;
const CAN_GO_TO_AM_INTERVAL = 3000;
const amRoot = Utils.replaceTenantNamePlaceHolder(process.env.REACT_APP_ANNOTATION_MANAGER!);

const isStitchinInProgress = false; //hc, check is needed!

const initStitchingManipulationRules: StitchingManipulationRules = {
  lockMovementX: false,
  lockMovementY: false,
  lockScalingX: false,
  lockScalingY: false,
  lockRotation: false
};

const Stitching: FC<RouteComponentProps<MatchTurbinePathParams>> = ({ history, match }) => {
  const [site, inspection, turbine, turbinePath] = useTurbinePath(match.params);
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    selectedBlade,
    selectedBladeSide,
    images,
    subsampleFactor,
    isStitchingFinished,
    isBladeSideStitched,
    isBladeSideRejected,
    isThumbnailsCreationInProgress,
    areImagesAvailable,
    completedSides,
    busy,
    isLoadingImages,
    isToolbarBusy,
    sequencesCache,
    canStitch,
    loading,
    totalNumberOfSequences
  } = state;

  const [showCompleteAllDialog, setShowCompleteAllDialog] = useState(false);
  const [precheckedKeys, setPrecheckedKeys] = useState<string[]>([]);
  const [canGoToAnnotationManager, setCanGoToAnnotationManager] = useState(false);
  const [stopCheckingForPermission, setStopCheckingForPermission] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [archivedTurbineId, setArchivedTurbineId] = useState<string | null>(null);
  const [restoreVisible, setRestoreVisible] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);

  const cacheId = `${selectedBlade}/${selectedBladeSide}`;
  const sequencePath = `${turbinePath}/${cacheId}`;

  const getImagesFromCache = useCallback(
    (id: string) => {
      return [...sequencesCache].find(x => x.id === id);
    },
    [sequencesCache]
  );

  const cacheSequence = getImagesFromCache(cacheId);
  const getImages: (url: string, isStitched: boolean, isRejected: boolean, pathEnding: string, id: string) => Promise<void> = useCallback(
    async (url, isStitched, isRejected, pathEnding, id) => {
      try {
        const response = await axios.get<ImagesResponse>(url);

        const { images, subsampleFactor } = response.data;

        // In case rank is not set populate it by sort by top
        if (images.filter(x => x.stackingRank === 0).length > 1) {
          var sorted = images.sort(sortImagesAscByTopFunction);

          sorted.forEach((image, index) => {
            image.stackingRank = index;
          });
        }

        dispatch({
          type: 'SET_IMAGES_AND_CACHE',
          images,
          subsampleFactor,
          newCache: {
            id,
            isStitched,
            isRejected,
            images,
            subsampleFactor
          }
        });

        if (!isStitched) {
          await axios.post(`Images/start-stitching/${pathEnding}`, null);
        }
      } catch (error) {
        dispatch({ type: 'CLEAR_BUSY_STATUS' });
      }
    },
    []
  );

  const checkTurbineInfo = useCallback(() => {
    if (turbinePath.length) {
      axios.get(`Images/${turbinePath}`).then(response => {
        const { anyUploadedSequence, isStitchinInProgress, completedSequences, notification, totalNumberOfSequences, isArchived: archived, turbineId: tId } = response.data;

        if (archived) {
          setIsArchived(true);
          setArchivedTurbineId(tId);
          setRestoreVisible(true);
          return;
        }

        if (!anyUploadedSequence) {
          history.push('/stitching-tool');
          return;
        }

        if (notification !== null && notification.length > 0) {
          ToastAnt(notification);
        }

        dispatch({
          type: 'SET_COMPLETED_SEQUENCES',
          completedSides: completedSequences || [],
          totalNumberOfSequences
        });

        dispatch({
          type: 'SET_CAN_STITCH',
          canStitch: !isStitchinInProgress
        });
      });
    }
  }, [history, turbinePath]);

  const checkSequenceInfo: (newBlade?: string, newSide?: string) => void = useCallback(
    async (newBlade, newSide) => {
      const blade = newBlade ? newBlade : selectedBlade;
      const side = newSide ? newSide : selectedBladeSide;
      setPrecheckedKeys([`${newBlade}/${newSide}`]);

      const pathEnding = `${turbinePath}/${blade}/${side}`;
      const checkBladeSideUrl = `Images/check-blade-side/${pathEnding}`;

      const id = `${blade}/${side}`;
      const cacheSequence = getImagesFromCache(id);

      if (cacheSequence) {
        dispatch({ type: 'SET_SEQUENCE_FROM_CACHE', cacheId: id });
        return;
      }

      const response = await axios.get(checkBladeSideUrl);
      const { isStitched, areImagesPrepared, message, inProgress, isRejected } = response.data;
      const imagesUrl = `Images/${pathEnding}`;

      dispatch({
        type: 'SET_SEQUENCE_INFO',
        areImagesPrepared,
        inProgress,
        isStitched,
        isRejected
      });

      if (message && message.length) {
        ToastAnt(message);
      }

      if (!areImagesPrepared) {
        return;
      }

      await getImages(imagesUrl, isStitched, isRejected, pathEnding, id);
    },
    [getImages, getImagesFromCache, selectedBlade, selectedBladeSide, turbinePath]
  );

  useEffect(() => {
    checkTurbineInfo();
  }, [checkTurbineInfo]);

  useEffect(() => {
    let id: any;

    if (isThumbnailsCreationInProgress) {
      id = setInterval(() => {
        checkSequenceInfo();
      }, thumbnailLongPoolingInterval);
    }

    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [checkSequenceInfo, isThumbnailsCreationInProgress]);

  function onSaveImage(images: any[], finish: boolean) {
    dispatch({ type: 'ON_SAVING_IMAGE_STARTED' });

    const url = `Images/${sequencePath}?isBladeSideStichingFinished=${finish}`;
    axios
      .post(url, images)
      .then(response => {
        const isStitchingFinished = response.data;

        dispatch({
          type: 'ON_SAVING_IMAGE_FINISHED',
          isStitchingFinished,
          finish,
          images
        });

        const message = finish ? `Stitching completed for ${cacheId} sequence.` : 'Successfully saved.';

        ToastAnt(message);
      })
      .catch(response => {
        ToastAnt('An error has occured.');
      });
  }

  function onCompleteAllSequences() {
    setShowCompleteAllDialog(false);

    dispatch({ type: 'SET_LOADING', loading: true });

    const url = `Images/complete-turbine/${turbinePath}`;

    axios
      .post(url, {})
      .then(response => {
        if (selectedBlade && selectedBladeSide) {
          dispatch({
            type: 'ON_SAVING_IMAGE_FINISHED',
            isStitchingFinished: true,
            finish: true,
            images
          });
        }

        checkTurbineInfo();

        ToastAnt('All sequences successfully completed.');
      })
      .catch(response => {
        ToastAnt('An error has occured.');
      })
      .finally(() => {
        dispatch({ type: 'SET_LOADING', loading: false });
      });
  }

  async function rejectSequenceHandler() {
    dispatch({ type: 'REJECT_SEQUENCE' });
    await axios.post(`Images/reject-sequence/${sequencePath}`, null);
  }

  function onSelectSide(blade: string, side: string) {
    dispatch({ type: 'ON_SELECT_BLADE_SIDE', blade, side });
    checkSequenceInfo(blade, side);
  }

  const chooseAnotherTurbine = () => {
    history.push('/stitching-tool');
  };

  const onRestoreRequest = async (restoreDays: number) => {
    try {
      const { data } = await axios.post('turbine/restore', {
        turbineId: archivedTurbineId,
        restoreDays
      });
      setRestoreVisible(false);

      // Only claim a restore was submitted when one actually was.
      if (isRestoreQueued(data)) {
        setSuccessVisible(true);
      } else {
        ToastAnt(describeRestoreNoOp(data));
      }
    } catch {
      ToastAnt('Failed to submit restore request. Please try again.');
      throw new Error('Restore request failed');
    }
  };

  const onImagesLoaded = useCallback(() => {
    dispatch({ type: 'ON_CANVAS_READY' });
  }, []);

  const goToAnnotationManager = () => {
    window.location.replace(`${amRoot}/windturbine/${turbinePath}`);
  };

  const checkForPermissionToGo = useCallback(async () => {
    try {
      const response = await axios.get<boolean>(`home/canUserOpenAnnotationManager/${turbinePath}`);
      setCanGoToAnnotationManager(response.data);
    } catch (error) {
      ToastAnt('An error occurred.');
      setStopCheckingForPermission(true);
    }
  }, [turbinePath]);

  useEffect(() => {
    let id: any;

    if (completedSides.length && !canGoToAnnotationManager && !stopCheckingForPermission) {
      id = setInterval(() => {
        checkForPermissionToGo();
      }, CAN_GO_TO_AM_INTERVAL);
    }

    return () => {
      if (id) {
        clearInterval(id);
      }
    };
  }, [canGoToAnnotationManager, checkForPermissionToGo, completedSides.length, stopCheckingForPermission]);

  if (isArchived) {
    const turbineDisplayPath = turbinePath.replace(/\//g, ' / ');
    return (
      <Fragment>
        <RestoreModal
          visible={restoreVisible}
          turbinePath={turbineDisplayPath}
          onRequest={onRestoreRequest}
          onCancel={chooseAnotherTurbine}
        />
        <RestoreSuccessModal
          visible={successVisible}
          turbinePath={turbineDisplayPath}
          onClose={chooseAnotherTurbine}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className={`${styles.mainContainer} container-fluid`}>
        <div className='row'>
          <div className={styles.left} style={{ backgroundColor: 'white' }}>
            <div className={styles.inspectionIdContainer}>
              {loading ? <span>Processing, please wait...</span> : <span>Inspection ID: {inspection}</span>}
            </div>
            <Dropdown className={styles.turbineActions}>
              <Dropdown.Toggle variant='success' id='dropdown-basic'>
                <EllipsisOutlined style={{ fontSize: '30px' }} />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item disabled={completedSides.length === totalNumberOfSequences} onClick={() => setShowCompleteAllDialog(true)}>
                  Complete all sequences
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <div className={`${styles.treeContainer} ${styles.noselect}`}>
              <TreeView
                turbine={turbine}
                onSelectSide={onSelectSide}
                completedSides={completedSides}
                disabled={(busy && !isThumbnailsCreationInProgress) || isLoadingImages || loading}
                showAdditional={false}
                preCheckedKeys={precheckedKeys}
                selectedBlade={selectedBlade}
                selectedSide={selectedBladeSide}
              />
            </div>
            <div className={styles.additionalNavigation}>
              <Button type='primary' disabled={!canGoToAnnotationManager} onClick={goToAnnotationManager}>
                Open in Annotation Module
              </Button>
            </div>
          </div>
          <div className={styles.right}>
            <div className={styles.stitchingBreadcrumb}>
              <span>{turbine}</span>
              {!!selectedBladeSide && <span className={styles.breadcrumbDelimeter}>{'>'}</span>}
              <span>{selectedBlade}</span>
              {!!selectedBladeSide && <span className={styles.breadcrumbDelimeter}>{'>'}</span>}
              <span>{selectedBladeSide}</span>
            </div>
            <CanvasContainer
              busy={busy}
              images={images}
              subsampleFactor={subsampleFactor}
              turbine={turbine}
              isStitchinInProgress={isStitchinInProgress}
              isStitchingFinished={isStitchingFinished}
              isSelectedBladeSideStitched={isBladeSideStitched}
              isSelectedBladeSideRejected={isBladeSideRejected}
              isBladeSideSelected={!!selectedBladeSide}
              onSaveImage={onSaveImage}
              onChooseAnotherTurbine={chooseAnotherTurbine}
              areImagesAvailable={areImagesAvailable}
              onImagesLoaded={onImagesLoaded}
              onRejectSequence={rejectSequenceHandler}
              isToolbarBusy={isToolbarBusy}
              canStitch={canStitch}
            />
          </div>
        </div>
      </div>
      <ConfirmationDialog
        show={showCompleteAllDialog}
        bodyMessage='Are you sure that you want to complete all sequences in turbine? Once stitching is
        completed you cannot stitch again.'
        onConfirm={() => onCompleteAllSequences()}
        onHide={() => setShowCompleteAllDialog(false)}
      />
    </Fragment>
  );
};

export default Stitching;
